import type { COMMENT_SORT_TYPE, COMMENT_TYPE } from '@/api';
import type { RootComment } from '@/types';

import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { AUTH_API, COMMENT_STATUS, UNAUTH_API } from '@/api';
import { queryKeys } from '@/constants/query-keys';

interface CommentListOptionsProps {
  id: number;
  commentType: COMMENT_TYPE;
  sortType?: COMMENT_SORT_TYPE;
  isLogin: boolean | undefined;
  userId: number | undefined;
}

interface LiveCommentOptionsProps {
  isLogin: boolean | undefined;
  commentTypeCd?: `${COMMENT_TYPE}`;
}

interface MyCommentOptionsProps {
  sortType?: COMMENT_SORT_TYPE;
  userId?: number;
  isLogin: boolean;
}

export const commentQueryOptions = {
  list: ({
    sortType,
    commentType,
    id,
    isLogin,
    userId,
  }: CommentListOptionsProps) =>
    queryOptions({
      queryKey: [queryKeys.commentList, id, commentType, sortType, isLogin],
      queryFn: () => {
        const params = {
          commentTypeCd: commentType,
          contentId: id,
          page: 1,
          size: 999,
          commentSort: sortType,
        };

        return isLogin
          ? AUTH_API.getCommentList(params)
          : UNAUTH_API.getCommentList(params);
      },
      enabled: isLogin !== undefined,
      placeholderData: keepPreviousData,
      select: ({ context }) => {
        if (!context || !context.contents) {
          return { commentList: [], totalCount: 0, totalRootCount: 0 };
        }

        const initialCommentList: RootComment[] = [];

        const commentList = context.contents.reduce((acc, comment) => {
          const transformedComment = {
            contentId: comment.content_id,
            commentId: comment.wello_comment_id,
            commentIdIdx: comment.content_id_idx,
            createdAt: comment.date_time,
            commentType: comment.comment_type_value,
            commentTypeCd: comment.comment_type_cd,
            thumbUpCount: comment.like_count,
            isDeleted: comment.comment_status_cd === COMMENT_STATUS.DELETED,
            contents: comment.contents,
            nickname: comment.nick_name,
            profileImage: comment.profile_image,
            isMine: comment.create_by === userId,
            isMyThumbUp: comment.like_yn,
            parentCommentId: comment.parent_wello_comment_id,
            rootCommentId: comment.root_wello_comment_id,
          };

          if (!comment.root_wello_comment_id) {
            acc.push({ ...transformedComment, childCommentList: [] });
          } else {
            const rootComment = acc.find(
              ({ commentId }) => commentId === comment.root_wello_comment_id,
            );
            rootComment?.childCommentList.push(transformedComment);
          }

          return acc;
        }, initialCommentList);

        return {
          commentList,
          totalCount: context.total_count,
          totalRootCount: context.root_total_count,
        };
      },
    }),
  liveList: ({ commentTypeCd, isLogin }: LiveCommentOptionsProps) =>
    queryOptions({
      queryKey: [queryKeys.liveComment, commentTypeCd, isLogin],
      queryFn: () => {
        return isLogin
          ? AUTH_API.getLiveCommentList({ commentTypeCd })
          : UNAUTH_API.getLiveCommentList({ commentTypeCd });
      },
      enabled: isLogin !== undefined,
      select: ({ context }) => {
        return {
          liveWelloCommentList: context?.live_wello_comment_list?.map(
            (liveComment) => {
              return {
                id: liveComment.content_id,
                idIdx: liveComment.content_id_idx,
                title: liveComment.title,
                contents: liveComment.contents,
                isBest: liveComment.best_yn,
                commentId: liveComment.wello_comment_id,
                commentCount: liveComment.comment_count,
                isMyThumbUp: liveComment.like_yn,
                commentTypeCd: liveComment.comment_type_cd,
                commentType: liveComment.comment_type_value,
                nickName: liveComment.nick_name,
                profileImage: liveComment.profile_image,
                thumbUpCount: liveComment.like_count,
                dateTime: liveComment.date_time,
              } satisfies LiveCommentQueryProps;
            },
          ),
        };
      },
    }),
  myList: ({ sortType, userId, isLogin }: MyCommentOptionsProps) =>
    queryOptions({
      queryKey: [queryKeys.commentList, sortType],
      queryFn: () => {
        const params = {
          page: 1,
          size: 999,
          commentSort: sortType,
        };

        return AUTH_API.getMyCommentList(params);
      },
      enabled: isLogin,
      placeholderData: keepPreviousData,
      select: ({ context }) => {
        if (!context || !context.contents) {
          return { commentList: [], totalCount: 0, totalRootCount: 0 };
        }

        const initialCommentList: RootComment[] = [];

        const commentList = context.contents.reduce((acc, comment) => {
          const transformedComment = {
            contentId: comment.content_id,
            commentId: comment.wello_comment_id,
            commentIdIdx: comment.content_id_idx,
            createdAt: comment.date_time,
            commentType: comment.comment_type_value,
            commentTypeCd: comment.comment_type_cd,
            thumbUpCount: comment.like_count,
            isDeleted: comment.comment_status_cd === COMMENT_STATUS.DELETED,
            contents: comment.contents,
            nickname: comment.nick_name,
            title: comment.title,
            profileImage: comment.profile_image,
            isMine: comment.create_by === userId,
            isMyThumbUp: comment.like_yn,
            parentCommentId: comment.parent_wello_comment_id,
            rootCommentId: comment.root_wello_comment_id,
          };

          if (!comment.root_wello_comment_id) {
            acc.push({ ...transformedComment, childCommentList: [] });
          } else {
            const rootComment = acc.find(
              ({ commentId }) => commentId === comment.root_wello_comment_id,
            );
            rootComment?.childCommentList.push(transformedComment);
          }

          return acc;
        }, initialCommentList);

        return {
          commentList,
          totalCount: context.total_count,
        };
      },
    }),
};

export interface LiveCommentQueryProps {
  id?: number;
  idIdx?: string;
  title?: string;
  nickName?: string;
  contents?: string;
  commentTypeCd?: string;
  isBest?: boolean;
  isLoading?: boolean;
  isMyThumbUp?: boolean;
  commentId?: number;
  commentType?: string;
  thumbUpCount?: number;
  dateTime?: string;
  commentCount?: number;
  isDeleted?: boolean;
  profileImage?: string;
  href?: string;
  onClickThumbUp?: VoidFunction;
  onClickComment?: VoidFunction;
  onClickDeleteButton?: VoidFunction;
}
